<template>
  <div class="main bg-primary">
    <div class="headerContainer mb-5">
      <TopDeck
        firstElement="Ajout recettes"
        secondElement="CSV"
        :coloredSecond="true"
        actionHeaderIcon="mdi-arrow-left"
        :actionheaderFunction="() => $router.push('/browse_base_recipes')"
        actionHeaderColor="accent"
        actionHeaderTextColor="white"
      />
    </div>

    <div class="headerWithAction bg-secondary rounded-xl mb-5">
      <!-- <input type="file" @change="handleFileUpload" accept=".csv" /> -->

      <v-file-input clearable label="Fichier CSV" variant="outlined" @change="handleFileUpload" accept=".csv"></v-file-input>

      <v-btn color="accent" variant="flat" class="ml-3" @click="exportBaseIngredientsCsv" :disabled="exporting">
        <v-icon class="mr-1">mdi-download</v-icon>
        Ingrédients de base
      </v-btn>
    </div>

    <div v-if="previewData.length > 0" class="preview">
      <div class="rowSpaceBetween">
        <h3>Prévisualisation ({{ previewData.length }} aliments)</h3>

        <v-btn
          color="accent"
          v-if="previewData.length > 0"
          rounded="lg"
          @click="importData"
          class="white-text-button"
        >
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span>Ajouter tout</span>
        </v-btn>
      </div>
      <div class="legend">
        <div class="legend-row">
          <v-chip size="x-small" class="ma-1" label color="success" text-color="white">Nom | qt g</v-chip>
          <span class="legend-text">ingrédient présent dans la base (fond vert)</span>
        </div>
        <div class="legend-row">
          <v-chip size="x-small" class="ma-1" label color="error" text-color="white">Nom | qt g</v-chip>
          <span class="legend-text">ingrédient manquant (fond rouge) : ligne non importable</span>
        </div>
      </div>
      <table class="rounded-xl">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Catégorie</th>
            <th>Image</th>
            <th>Notes</th>
            <th>Portions</th>
            <th>Poids (g)</th>
            <th>Kcal</th>
            <th>Ingrédients</th>
            
            <th>Glucides</th>
            <th>Gras</th>
            <th>Protéines</th>
            <th>Sels</th>
            <th>Sucres</th>
            <th>Valide pour import</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in previewData" :key="i">
            <td>{{ row.nom }}</td>
            <td>{{ row["catégorie"] }}</td>
            <td><img :src="row.image" alt="Image" width="100" v-if="row.image"/></td>
            <td>{{ row.notes }}</td>
            <td>{{ row.portions }}</td>
            <td>{{ row.poids }}</td>
            <td>{{ row.kcal }}</td>
            <td>
              <div class="chips-group column">
                <div
                  v-for="(item, idxIng) in getIngredientPairs(row)"
                  :key="idxIng"
                  class="chip-line"
                >
                  <v-chip
                    size="small"
                    class="ma-1"
                    label
                    :color="ingredientExists(item.name) ? 'success' : 'error'"
                    text-color="white"
                  >
                    {{ (item.name || 'Ingrédient ' + (idxIng + 1)) + ' | ' + (item.qty || '-') + ' g' }}
                  </v-chip>
                </div>
              </div>
            </td>
            <td>{{ row.glucides }}</td>
            <td>{{ row.gras }}</td>
            <td>{{ row["protéines"] }}</td>
            <td>{{ row.sels }}</td>
            <td>{{ row.sucres }}</td>
            <td>
              <span v-if="isRowValid(row)" style="color: green;">✅</span>
              <span v-else style="color: red;">❌</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

    <v-snackbar v-model="snack.show" :timeout="2500">
        {{ snack.text }}
    </v-snackbar>
    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
        {{ errorMessage }}
    </v-alert>
</template>

<script setup>
    import { ref, onMounted, watch, computed } from "vue";
    import { parseCsvFile, getBaseIngredients } from "@/service/base_ingredient_service";
    import TopDeck from '@/components/Utils/TopDeck.vue'
    import { useAuthStore } from "@/store/auth_store";
    import { addRecipeToBaseRecipes } from '@/service/base_recipe_service'

    const previewData = ref([]);

    const errorMessage = ref(null);
    const snack = ref({ show: false, text: "" });
    const authStore = useAuthStore();
    const baseIngredients = ref({});
    const baseIngredientIds = computed(() => new Set(Object.keys(baseIngredients.value || {})));
    const exporting = ref(false);

    // Sépare une cellule contenant plusieurs valeurs (séparées par virgule, point-virgule ou retour ligne)
    function splitList(value) {
        if (!value) return [];
        return String(value)
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    }

    // Regroupe les lignes CSV successives en recettes complètes en conservant tous les ingrédients/quantités
    function groupRowsIntoRecipes(rows) {
        const grouped = [];
        let current = null;

        for (const row of rows) {
        const hasAny = Object.values(row || {}).some((v) => String(v ?? "").trim() !== "");
        if (!hasAny) continue;

        const hasName = String(row["nom"] ?? "").trim().length > 0;

        if (hasName) {
            current = {
            nom: row["nom"] || "",
            catégorie: row["catégorie"] || "",
            image: row["image"] || "",
            notes: row["notes"] || "",
            portions: row["portions"] || "",
            poids: row["poids"] || "",
            kcal: row["kcal"] || "",
            glucides: row["glucides"] || "",
            gras: row["gras"] || "",
            protéines: row["protéines"] || "",
            sels: row["sels"] || "",
            sucres: row["sucres"] || "",
            ingrédients: [],
            quantités: [],
            items: [], // { name, qty }
            };
            grouped.push(current);
        }

        if (!current) continue;

        const ingList = splitList(row["ingrédients"]);
        const qtyList = splitList(row["qt"]);

        const max = Math.max(ingList.length, qtyList.length);
        for (let i = 0; i < max; i += 1) {
            const name = ingList[i] || "";
            const qty = qtyList[i] || "";
            current.items.push({ name, qty });
            if (name) current.ingrédients.push(name);
            if (qty) current.quantités.push(qty);
        }
        }

        return grouped;
    }

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const rows = await parseCsvFile(file);
        previewData.value = groupRowsIntoRecipes(rows);
    };


    function mapIngredientEntry([id, val]) {
      const nutriments = Array.isArray(val?.nutriments_map) ? val.nutriments_map : [];
      const findNutri = (key) => {
        const entry = nutriments.find((n) => n?.key === key);
        if (!entry) return '';
        const num = String(entry.value || '').match(/-?\d+(\.\d+)?/);
        return num ? num[0] : String(entry.value || '');
      };

      return {
        id,
        productName: val?.productName || '',
        productBrand: val?.productBrand || '',
        category: val?.category || '',
        servingQuantity: val?.servingQuantity ?? '',
        servingSize: val?.servingSize || '',
        kcal: findNutri('energy-kcal'),
        fat: findNutri('fat'),
        carbs: findNutri('carbohydrates'),
        protein: findNutri('proteins'),
        sugar: findNutri('sugar'),
        salt: findNutri('salt'),
      };
    }

    function toCsvValue(val) {
      const s = String(val ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    }

    async function exportBaseIngredientsCsv() {
      try {
        exporting.value = true;
        const entries = Object.entries(baseIngredients.value || {});
        if (!entries.length) {
          snack.value = { show: true, text: 'Aucun ingrédient de base à exporter.' };
          return;
        }

        const rows = entries.map(mapIngredientEntry);
        const headers = [
          'id', 'productName', 'productBrand', 'category', 'servingQuantity', 'servingSize',
          'kcal', 'fat', 'carbs', 'protein', 'sugar', 'salt'
        ];

        const csv = [headers.join(',')]
          .concat(rows.map((r) => headers.map((h) => toCsvValue(r[h])).join(',')))
          .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'base_ingredients.csv';
        a.click();
        URL.revokeObjectURL(url);
        snack.value = { show: true, text: 'Export CSV généré.' };
      } catch (e) {
        console.error('Export CSV ingrédients base échoué:', e);
        snack.value = { show: true, text: '❌ Export CSV échoué' };
      } finally {
        exporting.value = false;
      }
    }


    // Transforme une ligne validée en payload pour base recipes
    function parseNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    }

    function mapRowToBaseRecipe(row) {
      const items = Array.isArray(row.items) ? row.items : getIngredientPairs(row);
      const ingredients = items
        .map((it) => ({ id: String(it.name || '').trim(), grams: parseNumber(it.qty) }))
        .filter((it) => it.id && it.grams > 0);

      const totalGrams = ingredients.reduce((s, it) => s + it.grams, 0);

      return {
        title: row.nom || '',
        category: row["catégorie"] || '',
        imageUrl: row.image || row.url || '',
        url: row.image || row.url || '',
        notes: row.notes || '',
        servings: parseNumber(row.portions) || 1,
        ingredients,
        totals: {
          grams: totalGrams,
          kcal: parseNumber(row.kcal),
          protein: parseNumber(row["protéines"]),
          carbs: parseNumber(row.glucides),
          fat: parseNumber(row.gras),
          sugars: parseNumber(row.sucres),
          salt: parseNumber(row.sels),
        },
        baseRecipe: true,
        date: Date.now(),
      };
    }

    const importData = async () => {
      try {
        errorMessage.value = null;
        const validRows = previewData.value.filter((row) => isRowValid(row));
        if (validRows.length === 0) {
          snack.value = { show: true, text: "Aucune ligne valide à importer." };
          return;
        }

        let added = 0;
        let skipped = 0;
        for (const row of validRows) {
          const payload = mapRowToBaseRecipe(row);
          const res = await addRecipeToBaseRecipes(authStore.userid, payload);
          if (res?.existed) {
            skipped += 1;
          } else {
            added += 1;
          }
        }

        const msg = added > 0
          ? `✅ ${added} recette(s) ajoutée(s) aux recettes de base${skipped ? ` • ${skipped} déjà présentes` : ''}`
          : `Aucune recette ajoutée${skipped ? ` • ${skipped} déjà présentes` : ''}`;
        snack.value = { show: true, text: msg };
      } catch (e) {
        console.error('Erreur import base recipes:', e);
        errorMessage.value = "Erreur lors de l'import des recettes.";
        snack.value = { show: true, text: "❌ Import interrompu" };
      }
    };


    /**
     * Vérifie si une ligne CSV est valide pour import
     */
    function ingredientExists(name) {
      const key = String(name || "").trim();
      if (!key) return false;
      return baseIngredientIds.value.has(key);
    }

    function isRowValid(row) {
        const requiredFields = [
            //Tous les champs
            "nom",
            "kcal",
            "gras",
            "glucides",
            "protéines",
            "ingrédients",  
            "quantités"
        ];

        const basicsFilled = requiredFields.every(
        (field) => row[field] !== undefined && row[field] !== null && row[field] !== ""
        );

        const ingredientList = Array.isArray(row.ingrédients) ? row.ingrédients : splitList(row["ingrédients"]);
        const allIngredientsPresent = ingredientList.length > 0 && ingredientList.every(ingredientExists);

        return basicsFilled && allIngredientsPresent;
    }

    // Paire ingrédients et quantités par index pour un rendu aligné
    function getIngredientPairs(row) {
        if (Array.isArray(row.items)) return row.items;

        const ingredients = Array.isArray(row.ingrédients) ? row.ingrédients : splitList(row["ingrédients"]);
        const quantities = Array.isArray(row.quantités)
        ? row.quantités
        : splitList(row["qt"]);
        const max = Math.max(ingredients.length, quantities.length);

        return Array.from({ length: max }, (_, i) => ({
        name: ingredients[i] || '',
        qty: quantities[i] || '',
        }));
    }

    onMounted(() => {
        watch(
        () => authStore.userInfos,
        async () => {
            if (!authStore?.userid) return
                try {
                const baseData = await getBaseIngredients()
                baseIngredients.value = baseData;
            } catch (e) {
                errorMessage.value = "Erreur lors du chargement des ingrédients."
            }
        },
        { immediate: true }
        )
    })
</script>

<style scoped>
  .headerWithAction {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: auto;
    padding: 2rem;
  }

  .preview table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
  }

  .chips-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .chips-group.column {
    flex-direction: column;
    align-items: flex-start;
  }

  .chip-line {
    display: flex;
    align-items: center;
  }

  .legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0.75rem 0 0.5rem;
    padding: 8px 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 12px;
    font-size: 0.9rem;
    color: rgb(var(--v-theme-on-background));
  }

  .legend-row {
    display: flex;
    align-items: center;
  }

  .legend-text {
    margin-left: 8px;
  }

  .preview th,
  .preview td {
    border: 1px solid rgb(var(--v-theme-secondary));
    padding: 8px;
    text-align: center;
  }

  .preview th {
    background: rgb(var(--v-theme-tertiary));
  }
</style>
